<template lang="html">
  <section>
    <name-input
      :all-des="allDes"
      :is-ready="true"
      :name="storedDataElement.name"
      :should-focus="!storedDataElement.name"
      :de-id="storedDataElement.id"
      @on-name-input-changed="onDeNameChanged"
    ></name-input>

    <element-description
      :elem-description="storedDataElement.description"
      @on-element-description-changed="onDeDescriptionChanged"
    ></element-description>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ getMessageText(messages.SCALE) }}
      </label>
      <div class="expression-wrapper">
        <div class="expression">
          <b-form-select
            v-model="storedDataElement.scale"
            :options="scales"
            size="sm"
          ></b-form-select>
        </div>
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ getMessageText(messages.DATA_TYPE) }}
      </label>
      <div class="expression-wrapper">
        <div class="expression">
          <text-input
            ref="textInputItem"
            v-model="initialDataType"
            :type="'text'"
            :hasLabel="false"
            @on-text-input-blur="onInputChanged"
          />
        </div>
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ $t("accountIds") }}
      </label>
      <div class="expression-wrapper">
        <div class="expression">
          <expressions
            id="accountIdExpression"
            :data-elements="allSources"
            :data-type="'string'"
            :is-list-expression="true"
            :is-global="true"
            :content-type="appType"
            :initial-expression="storedDataElement.accountIds"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ getMessageText(messages.START_DATE) }}
      </label>
      <div class="expression-wrapper">
        <div class="expression">
          <expressions
            id="startDateExpression"
            :data-elements="allSources"
            :data-type="'date'"
            :is-global="true"
            :content-type="appType"
            :initial-expression="storedDataElement.startDate"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ getMessageText(messages.END_DATE) }}
      </label>
      <div class="expression-wrapper">
        <div class="expression">
          <expressions
            id="endDateExpression"
            :data-elements="allSources"
            :data-type="'date'"
            :is-global="true"
            :content-type="appType"
            :initial-expression="storedDataElement.endDate"
            @on-expression-field-valid="onConfigExpressionValid"
          />
        </div>
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ $t("sort") }}
      </label>
      <div class="query-sort">
        <sort-field
          :initial-sort="initialSort"
          :fields="fields"
          @on-sort-fields-change="onSortFieldsChange"
        />
      </div>
    </div>

    <div class="sales-fields-container">
      <label class="query-label">
        {{ $t("limit") }}
      </label>
      <div class="query-limit">
        <b-form-input
          :value="initialLimit"
          v-model="storedDataElement.limit"
          type="number"
          min="0"
        ></b-form-input>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import { mapState, mapActions } from "vuex";
  import { MUTATION_NAME } from "@/static/global-variables";
  import SortField from "@/components/queries/crm-queries/SortField";
  import TextInput from "@/components/TextInput";
  import Expressions from "@/components/Expressions"
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";
  import utils from "@/utils";

  export default  {
    name: "sales-edit",
    components: {
      Expressions,
      TextInput,
      SortField,
      NameInput,
      ElementDescription
    },
    props: {
      appId: {
        type: String
      },
      de: {
        type: Object
      },
      allDes: {
        type: Object
      },
      appType: {
        type: String
      }
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      scales(){
        return [
          {
            text: this.getMessageText(this.messages.Weekly),
            value: "Week_vod"
          },
          {
            text: this.getMessageText(this.messages.Monthly),
            value: "Month_vod"
          }
        ];
      },
      fields() {
        return utils.getSalesDataQueryFields();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      allSources() {
        let output = null;
        if (this.appContent) {
          output = this.appContent.dataElements || {};
        } else {
          console.error(`No app content found for appId ${this.appId}`);
        }
        // need to remove this when elm is ready
        delete output[this.de.id];
        return output;
      },
    },
    mounted() {
      this.init();
    },
    data() {
      return {
        storedDataElement: {
          appId: this.appId,
          id: this.de.id,
          description: this.de.description,
          type: this.de.type,
          name: this.de.name,
          scale: this.de.scale || "",
          dataType: this.de.dataType || "",
          accountIds: this.de.accountIds || null,
          startDate: this.de.startDate || null,
          endDate: this.de.endDate || null,
          sort: Array.isArray(this.de.sort)? this.de.sort : [],
          limit: this.de.limit
        },
        initialDataType: this.de.dataType,
        initialSort: Array.isArray(this.de.sort)? this.de.sort : [], // for initial setup for sort and avoid loop on update
        initialLimit: this.de.limit,  // for initial setup for limit value and avoid loop on update
      };
    },
    methods: {
      ...mapActions({
        setDataElement: MUTATION_NAME.SET_EDIT_DATA_ELEMENT
      }),
      init() {
        this.setDataElement(this.storedDataElement);
      },
      onSortFieldsChange(sort) {
        this.storedDataElement.sort = sort;
        this.setDataElement(this.storedDataElement);
      },
      onInputChanged(val) {
        this.storedDataElement.dataType = val;
      },

      onConfigExpressionValid(ctx) {
        if(ctx.id === "startDateExpression") {
          this.storedDataElement.startDate = ctx.status ? ctx.confExpression : null;
        } else if(ctx.id === "endDateExpression") {
          this.storedDataElement.endDate = ctx.status ? ctx.confExpression : null;
        } else if (ctx.id === "accountIdExpression") {
          this.storedDataElement.accountIds = ctx.status ? ctx.confExpression : null;
        }
      },
      onDeNameChanged(isValidName, nameInput) {
        this.storedDataElement.name = nameInput;
        this.$emit("on-de-name-changed", isValidName);
        this.setDataElement(this.storedDataElement);
      },
      onDeDescriptionChanged(input) {
        this.storedDataElement.description = input;
        this.setDataElement(this.storedDataElement);
      }
    }
  }
</script>

<style scoped lang="scss">
  .sales-fields-container {
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
    margin-top: 1rem;
    align-items: baseline;

    .expression-wrapper {
      width: 100%;

      .m-text-input__container {
        width: 100%;
      }
    }

    .expression,
    .label {
      display: flex;
      align-items: center;
      min-height: 2.5rem;
    }
  }
  .sales-fields-container {
    min-width: 45rem;
  }

  .sales-fields-container:nth-child(1) {
    margin-top: 0;
  }

  .query-label {
    min-width: 6rem;
    padding-right: 0.5rem;
    margin-bottom: 0;
  }

  .query-limit {
    max-width: 20%;
  }

  .query-limit input {
    font-size: 0.75rem;
  }

  .query-sort {
    flex: 1 0 auto;
    width: 100%;
  }
</style>
